// Extend AxiosRequestConfig to include our custom properties
declare module 'axios' {
    export interface AxiosRequestConfig {
        skipAuth?: boolean
        optionalAuth?: boolean
        skipAuthErrorHandling?: boolean
    }
}

export interface ApiMessage {
    messageDesc: string
    messageType: string
}

export interface ApiError {
    message: string
    messageType: string
}

export interface ProblemDetail {
    type: string
    title: string
    status: number
    detail: string
    instance: string
    properties?: Record<string, unknown>
}
/**
 * @description Strategy for handling errors
 * @enum {string}
 * @property {string} THROW - Throw an error
 * @property {string} RETURN - Return an error
 */
export enum ErrorHandlingStrategy {
    THROW = 'THROW',
    RETURN = 'RETURN'
}

export interface ToastMessage {
    message: string
    type: 'success' | 'error' | 'warning' | 'info'
    title?: string
    duration?: number
}
export const SuccessStatus = {
    // Specific status codes
    OK: 200, // Standard success response
    CREATED: 201, // Resource successfully created
    ACCEPTED: 202, // Request accepted but not yet completed
    NO_CONTENT: 204, // Request successful, no content to return

    // Status ranges
    ANY_SUCCESS: (status: number) => status >= 200 && status < 300, // Any 2XX status
    ANY_REDIRECT: (status: number) => status >= 300 && status < 400, // Any 3XX status
    ANY_CLIENT_ERROR: (status: number) => status >= 400 && status < 500, // Any 4XX status
    ANY_SERVER_ERROR: (status: number) => status >= 500 && status < 600 // Any 5XX status
} as const

// Type for specific success status codes
export type SuccessStatusType =
    (typeof SuccessStatus)[keyof typeof SuccessStatus]

export interface ApiResponse<T> {
    data: T
    status: number
    message?: ApiMessage[]
    error?: ApiError[]
    messageType?: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS'
    dataType?: 'LIST' | 'OBJECT' | 'EMPTY'
    problemDetail?: ProblemDetail
}
export enum MessageLevel {
    ALL = 'ALL',
    ERRORS_AND_INFO_ONLY = 'ERRORS_ONLY',
    ERRORS = 'ERRORS',
    SUCCESS_ONLY = 'SUCCESS_ONLY',
    NONE = 'NONE'
}

export interface ApiConfig {
    messageLevel?: MessageLevel
    errorHandling?: ErrorHandlingStrategy
    timeout?: number
    retryAttempts?: number
}

export interface ApiHandlerOptions extends ApiConfig {
    baseURL: string
    getAccessToken: () => string | null
    getRefreshToken: () => string | null
    getNewAccessToken: (
        oldAccessToken: string,
        refreshToken: string
    ) => Promise<string | null>
    onAuthError: () => void
    showMessage: (message: ToastMessage) => void
}
